/**
 * <TutorStatementAid> — l'aide à la correction sous l'énoncé, au masteur seul (molecule).
 *
 * L'ancien `common/components/material/MaterialCorrectionDisplay.js`, côté tuteur : un seul bouton, trois
 * états — « Génération de la correction... » (il tourne), « Aide à la correction » / « Masquer l'aide »,
 * « Réessayer » —, le bandeau « L'aide à la correction n'a pas pu être générée. » quand elle n'a pas pu
 * l'être, et, ouverte, le corrigé (les notions clés puis la correction) suivi de l'avis du masteur
 * (« Correction utile » / « Correction peu utile »).
 *
 * Contrôlée : l'état arrive en props (`aid`), chaque geste repart.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"
import { View } from "react-native"

import { type UICorrectionFeedback } from "../../../ports/primary/ui/types/studentActivity"
import { type UITutorStatementAid } from "../../../ports/primary/ui/types/tutorLive"
import { Banner } from "../../atoms/Banner"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Stack } from "../../atoms/containers/Stack"
import { CorrectionFeedback } from "../CorrectionFeedback"
import { RichText } from "../RichText"

export type TutorStatementAidProps = {
    aid: UITutorStatementAid
    /** Ouvrir ou replier le corrigé. */
    onToggle?: () => void
    /** « Réessayer » après un échec. */
    onRetry?: () => void
    onFeedback?: (feedback: UICorrectionFeedback) => void
    testID?: string
}

const TEXTS = {
    generating: "Génération de la correction...",
    show: "Aide à la correction",
    hide: "Masquer l'aide",
    retry: "Réessayer",
    failed: "L'aide à la correction n'a pas pu être générée.",
    feedback: { question: "Cette correction t'aide-t-elle ?", yes: "Correction utile", no: "Correction peu utile" },
}

export function TutorStatementAid(props: TutorStatementAidProps) {
    const { aid, onToggle, onRetry, onFeedback, testID } = props
    return (
        <Stack gap="2" testID={testID}>
            {aid.state === "generating" && <TextButton label={TEXTS.generating} size="sm" loading color="text-muted" />}
            {aid.state === "ready" && (
                <TextButton
                    label={aid.open ? TEXTS.hide : TEXTS.show}
                    size="sm"
                    color={aid.open ? "text-muted" : undefined}
                    onPress={onToggle}
                />
            )}
            {aid.state === "failed" && (
                <>
                    <Banner message={TEXTS.failed} icon="info-circle" color="text-muted" align="start" />
                    <TextButton label={TEXTS.retry} size="sm" loading={aid.retrying === true} onPress={onRetry} />
                </>
            )}
            {aid.state === "ready" && aid.open && (
                <View style={styles.sheet}>
                    <RichText value={aid.markdown} compact />
                    {aid.feedback !== undefined && onFeedback != null && (
                        <CorrectionFeedback value={aid.feedback} onChange={onFeedback} texts={TEXTS.feedback} />
                    )}
                </View>
            )}
        </Stack>
    )
}

const styles = StyleSheet.create((theme) => ({
    sheet: {
        gap: theme.spacing["2"],
        padding: theme.spacing["3"],
        borderRadius: theme.radius.md,
        backgroundColor: theme.colors["surface-subtle"],
    },
}))
